import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, firstValueFrom, of } from 'rxjs';
import { ErrorFrontend } from '@core/errors';
import type { Recipient } from '@core/models';
import type { RecipientPayload } from '../../pages/orientador/models';

/**
 * Recipient Service
 * Manages authorized third-party recipients
 */
@Injectable({
  providedIn: 'root',
})
export class RecipientService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = 'intake/recipients';

  /**
   * Create new recipient
   */
  async create(data: RecipientPayload): Promise<Recipient> {
    return await firstValueFrom(
      this.http.post<Recipient>(`${this.baseUrl}/`, data).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Look up a recipient by document type and number. Returns null if not found.
   * Both halves identify the person: the number alone is ambiguous across types.
   */
  async byDocument(documentType: string, documentId: string): Promise<Recipient | null> {
    return await firstValueFrom(
      this.http
        .get<Recipient>(`${this.baseUrl}/by_document/`, {
          params: { document_type: documentType, document_id: documentId },
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 404) return of(null);
            throw new ErrorFrontend(error);
          }),
        ),
    );
  }
}
